import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { LogIn, UserPlus, Building2, ArrowRight, Eye, Sparkles } from "lucide-react";
import { agentAdvancerSenzaSfondo } from '../images';

const LandingAccesso = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 pb-28 md:pb-8">
      {/* Header con Logo */}
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-xl p-2">
              <img 
                src={agentAdvancerSenzaSfondo} 
                alt="InLeads Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-gray-900">InLeads</h1>
              <p className="text-blue-600 font-medium text-sm">Recruitment App</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-md mx-auto">
          {/* Welcome Section */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Benvenuto su InLeads
            </h2>
            <p className="text-lg text-gray-600 mb-2">
              Recruitment App
            </p>
            <p className="text-gray-500">
              Accedi o crea il tuo profilo per iniziare
            </p>
          </div>

          {/* Action Cards */}
          <div className="space-y-4 px-4 sm:px-0">
            {/* Accedi Card */}
            <Card className="border-0 shadow-xl rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                      <LogIn className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">Accedi</h3>
                      <p className="text-gray-600 text-sm">Hai già un account?</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl px-6 py-3"
                  >
                    Entra
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Registrati Card */}
            <Card className="border-0 shadow-xl rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                      <UserPlus className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">Registrati</h3>
                      <p className="text-gray-600 text-sm">Nuovo utente? Inizia qui</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/registrati')}
                    variant="outline"
                    className="w-full sm:w-auto border-green-300 text-green-600 hover:bg-green-50 rounded-xl px-6 py-3"
                  >
                    Inizia
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Demo Card */}
            <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-xl rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-xl font-semibold text-gray-900">Guarda la Demo</h3>
                        <Sparkles className="w-4 h-4 text-amber-500" />
                      </div>
                      <p className="text-gray-600 text-sm">Esplora InLeads liberamente</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/demo')}
                    className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl px-6 py-3 shadow-lg hover:shadow-xl transition-all"
                    title="Scopri come funziona InLeads in meno di 1 minuto"
                  >
                    Demo Live
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Benefits Section */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm mb-6">
              Unisciti a centinaia di professionisti immobiliari
            </p>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4">
                <div className="text-2xl font-bold text-blue-600">348</div>
                <div className="text-xs text-gray-600">Candidati Attivi</div>
              </div>
              <div className="p-4">
                <div className="text-2xl font-bold text-green-600">104</div>
                <div className="text-xs text-gray-600">Agenzie Partner</div>
              </div>
              <div className="p-4">
                <div className="text-2xl font-bold text-purple-600">95%</div>
                <div className="text-xs text-gray-600">Soddisfazione</div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 text-center text-xs text-gray-400">
            <p>© 2024 InLeads. Tutti i diritti riservati.</p>
            <p className="mt-2">Sicuro • Veloce • Professionale</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingAccesso;